import { useCallback, useState } from "react";
import { Mic } from "lucide-react";
import { toast } from "sonner";
import {
  useRecorder,
  useRecordingSupport,
  useSpeaker,
  transcribe,
} from "@/components/assessment/voice/hooks";
import {
  VoicePanelHeader,
  FollowUpBubble,
  TranscriptBubble,
  ApplyFieldsButton,
} from "@/components/assessment/voice/Bubbles";
import RecordingControls from "@/components/assessment/voice/RecordingControls";

const INITIAL_PROMPT =
  "Hi — tap the mic and tell me about what you do now and what's changing. Take your time.";

/**
 * Voice interview widget. Floats as a button bottom-left; clicking expands
 * a small panel with a record/stop toggle, transcript, AI follow-up and
 * an "Apply to form" button. Audio is sent once to the backend, then
 * discarded — only the resulting transcript is logged.
 */
export default function VoiceInterview({ context, onApplyFields }) {
  const [open, setOpen] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [followUp, setFollowUp] = useState(INITIAL_PROMPT);
  const [lastFields, setLastFields] = useState(null);

  const supportsRecording = useRecordingSupport();
  const speak = useSpeaker();

  const handleBlob = useCallback(
    async (blob) => {
      setTranscribing(true);
      try {
        const data = await transcribe(blob, context);
        if (!data) return;
        setTranscript(data.transcript || "");
        setFollowUp(data.follow_up || "");
        setLastFields(data.fields || {});
        if (data.follow_up) speak(data.follow_up);
        if (data.fields && Object.keys(data.fields).length === 0) {
          toast.message(
            "Got the words but couldn't tag fields — try giving a bit more detail.",
          );
        }
      } finally {
        setTranscribing(false);
      }
    },
    [context, speak],
  );

  const { recording, start, stop } = useRecorder({ onBlob: handleBlob });

  const apply = () => {
    if (!lastFields || Object.keys(lastFields).length === 0) {
      toast.message("Nothing to apply yet.");
      return;
    }
    onApplyFields?.(lastFields);
    toast.success(
      `Applied ${Object.keys(lastFields).length} section(s) to the form.`,
    );
    setLastFields(null);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 left-6 z-40 inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-4 py-3 text-sm font-medium shadow-xl hover:bg-emerald-700 hover:-translate-y-0.5 transition-all"
        data-testid="voice-open-button"
        aria-label="Open the voice interview agent"
      >
        <Mic className="h-4 w-4" /> Talk to me instead
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-6 left-6 z-40 w-[360px] max-w-[95vw] rounded-2xl border border-slate-200 bg-white shadow-2xl"
      data-testid="voice-panel"
      role="dialog"
      aria-label="Voice interview agent"
    >
      <VoicePanelHeader
        onClose={() => {
          stop();
          setOpen(false);
        }}
      />
      <div className="px-4 py-3 space-y-3 text-sm">
        <FollowUpBubble text={followUp} onReplay={() => speak(followUp)} />
        <TranscriptBubble text={transcript} />
        <ApplyFieldsButton fields={lastFields} onApply={apply} />
        <div className="flex items-center justify-center gap-3 pt-1">
          <RecordingControls
            recording={recording}
            transcribing={transcribing}
            supportsRecording={supportsRecording}
            onStart={start}
            onStop={stop}
          />
        </div>
        <p className="text-[11px] text-slate-400 text-center leading-relaxed pt-1">
          We send the audio once to transcribe it, then discard it. Only the
          text is logged for your account.
        </p>
      </div>
    </div>
  );
}
